import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";

import type { IPayrollRunItem } from "@/services/(company)/payrollRunsApi";
import { PAYMENT_METHOD_LABELS } from "@/utils/enums/payroll.enum";

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });


interface ITotals {
  gross: number;
  overtime: number;
  deductions: number;
  contrib: number;
  net: number;
  count: number;
}

interface IMethodBreakdown {
  method: string;
  label: string;
  count: number;
  net: number;
}

function buildHtml(
  items: IPayrollRunItem[],
  periodLabel: string,
  generatedAt: string,
  runName?: string,
): string {
  const currency = items[0]?.currencyCode ?? "";

  const totals: ITotals = items.reduce(
    (acc, r) => ({
      gross: acc.gross + toNum(r.grossPayroll),
      overtime: acc.overtime + toNum(r.overtimePay),
      deductions: acc.deductions + toNum(r.totalDeductions),
      contrib: acc.contrib + toNum(r.totalStatutoryEmployee),
      net: acc.net + toNum(r.netPay),
      count: acc.count + 1,
    }),
    { gross: 0, overtime: 0, deductions: 0, contrib: 0, net: 0, count: 0 },
  );

  const methodMap = new Map<string, IMethodBreakdown>();
  for (const r of items) {
    const key = r.paymentMethod ?? "UNKNOWN";
    const existing = methodMap.get(key);
    if (existing) {
      existing.count += 1;
      existing.net += toNum(r.netPay);
    } else {
      methodMap.set(key, {
        method: key,
        label: PAYMENT_METHOD_LABELS[key] ?? key,
        count: 1,
        net: toNum(r.netPay),
      });
    }
  }
  const methodBreakdown = Array.from(methodMap.values());

  const statusBg: Record<string, string> = {
    PAID: "#dcfce7",
    HOLD: "#fee2e2",
    EXCLUDED: "#f3f4f6",
  };
  const statusColor: Record<string, string> = {
    PAID: "#166534",
    HOLD: "#991b1b",
    EXCLUDED: "#6b7280",
  };

  // Shared cell border style — explicit borders survive image cropping
  const cellBorder = "border:1px solid #e5e7eb";
  const noWrap = "white-space:nowrap";

  const rows = items
    .map((r) => {
      const status = String(r.employeeStatus ?? "PENDING").toUpperCase();
      const bg = statusBg[status] ?? "#fefce8";
      const color = statusColor[status] ?? "#854d0e";
      const label = status.charAt(0) + status.slice(1).toLowerCase();
      return `
      <tr>
        <td style="${cellBorder};padding:6px 8px">${r.employee.fullName}<br/><span style="color:#9ca3af;font-size:10px">${r.employee.employeeCode}</span></td>
        <td style="${cellBorder};padding:6px 8px">${r.branchName ?? "—"}<br/><span style="color:#9ca3af;font-size:10px">${r.departmentName ?? "—"}</span></td>
        <td style="${cellBorder};padding:6px 8px;text-align:right;${noWrap}">${r.currencyCode} ${fmt(toNum(r.grossPayroll))}</td>
        <td style="${cellBorder};padding:6px 8px;text-align:right;${noWrap}">${toNum(r.overtimePay) ? `${r.currencyCode} ${fmt(toNum(r.overtimePay))}` : "—"}</td>
        <td style="${cellBorder};padding:6px 8px;text-align:right;color:#ef4444;${noWrap}">${r.currencyCode} ${fmt(toNum(r.totalDeductions))}</td>
        <td style="${cellBorder};padding:6px 8px;text-align:right;color:#ef4444;${noWrap}">${r.currencyCode} ${fmt(toNum(r.totalStatutoryEmployee))}</td>
        <td style="${cellBorder};padding:6px 8px;text-align:right;font-weight:600;${noWrap}">${r.currencyCode} ${fmt(toNum(r.netPay))}</td>
        <td style="${cellBorder};padding:6px 8px;${noWrap}">${r.paymentMethod ?? "—"}</td>
        <td style="${cellBorder};padding:6px 8px"><span style="background:${bg};color:${color};padding:2px 6px;border-radius:4px;font-size:10px;font-weight:600">${label}</span></td>
      </tr>`;
    })
    .join("");

  const methodRows = methodBreakdown
    .map(
      (m) => `
      <tr>
        <td style="${cellBorder};padding:6px 10px">${m.label}</td>
        <td style="${cellBorder};padding:6px 10px;text-align:center">${m.count}</td>
        <td style="${cellBorder};padding:6px 10px;text-align:right;font-weight:600;${noWrap}">${currency} ${fmt(m.net)}</td>
      </tr>`,
    )
    .join("");

  return `
<div style="font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:#111827;background:#fff;padding:24px;min-width:1000px;box-sizing:border-box">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px">
    <div>
      <div style="font-size:18px;font-weight:700;color:#111827">Employee Earnings Report</div>
      <div style="font-size:12px;color:#6b7280;margin-top:2px">${runName ? `${runName} · ` : ""}${periodLabel}</div>
    </div>
    <div style="text-align:right;font-size:11px;color:#9ca3af">
      Generated ${generatedAt}
    </div>
  </div>

  <!-- Summary cards -->
  <div style="display:flex;gap:8px;margin-bottom:16px">
    <div style="flex:1;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 12px">
      <div style="font-size:9px;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:.5px">Employees</div>
      <div style="font-size:16px;font-weight:700;margin-top:2px">${totals.count}</div>
    </div>
    <div style="flex:1;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 12px">
      <div style="font-size:9px;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:.5px">Gross Payroll</div>
      <div style="font-size:16px;font-weight:700;margin-top:2px;white-space:nowrap">${currency} ${fmt(totals.gross)}</div>
    </div>
    <div style="flex:1;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 12px">
      <div style="font-size:9px;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:.5px">Total Deductions</div>
      <div style="font-size:16px;font-weight:700;color:#ef4444;margin-top:2px;white-space:nowrap">${currency} ${fmt(totals.deductions)}</div>
    </div>
    <div style="flex:1;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 12px">
      <div style="font-size:9px;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:.5px">Contributions</div>
      <div style="font-size:16px;font-weight:700;color:#ef4444;margin-top:2px;white-space:nowrap">${currency} ${fmt(totals.contrib)}</div>
    </div>
    <div style="flex:1;background:#111827;border-radius:6px;padding:8px 12px">
      <div style="font-size:9px;color:#9ca3af;font-weight:600;text-transform:uppercase;letter-spacing:.5px">Net Pay</div>
      <div style="font-size:16px;font-weight:700;color:#fff;margin-top:2px;white-space:nowrap">${currency} ${fmt(totals.net)}</div>
    </div>
  </div>

  <!-- Payment method breakdown -->
  <div style="margin-bottom:16px">
    <div style="font-size:12px;font-weight:600;color:#374151;margin-bottom:6px">Payment Method Breakdown</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px">
      <thead>
        <tr style="background:#f3f4f6">
          <th style="text-align:left;padding:6px 10px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Method</th>
          <th style="text-align:center;padding:6px 10px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Employees</th>
          <th style="text-align:right;padding:6px 10px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Net Pay</th>
        </tr>
      </thead>
      <tbody>
        ${methodRows}
        <tr style="background:#f9fafb;font-weight:700">
          <td style="padding:6px 10px;border:1px solid #e5e7eb;border-top:2px solid #374151">Total</td>
          <td style="text-align:center;padding:6px 10px;border:1px solid #e5e7eb;border-top:2px solid #374151">${totals.count}</td>
          <td style="text-align:right;padding:6px 10px;border:1px solid #e5e7eb;border-top:2px solid #374151;white-space:nowrap">${currency} ${fmt(totals.net)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Employee details table -->
  <div style="font-size:12px;font-weight:600;color:#374151;margin-bottom:6px">Employee Details</div>
  <table id="earnings-detail-table" style="width:100%;border-collapse:collapse;font-size:11px">
    <thead>
      <tr style="background:#f3f4f6">
        <th style="text-align:left;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Employee</th>
        <th style="text-align:left;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Branch / Dept</th>
        <th style="text-align:right;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb;white-space:nowrap">Gross</th>
        <th style="text-align:right;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb;white-space:nowrap">OT</th>
        <th style="text-align:right;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb;white-space:nowrap">Deductions</th>
        <th style="text-align:right;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb;white-space:nowrap">Contrib.</th>
        <th style="text-align:right;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb;white-space:nowrap">Net Pay</th>
        <th style="text-align:left;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Method</th>
        <th style="text-align:left;padding:6px 8px;font-weight:600;color:#6b7280;font-size:10px;text-transform:uppercase;letter-spacing:.5px;border:1px solid #e5e7eb">Status</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <!-- Totals row -->
      <tr style="background:#f9fafb;font-weight:700">
        <td colspan="2" style="padding:8px 8px;font-size:11px;border:1px solid #e5e7eb;border-top:2px solid #111827">Totals (${totals.count} employees)</td>
        <td style="text-align:right;padding:8px 8px;border:1px solid #e5e7eb;border-top:2px solid #111827;white-space:nowrap">${currency} ${fmt(totals.gross)}</td>
        <td style="text-align:right;padding:8px 8px;border:1px solid #e5e7eb;border-top:2px solid #111827;white-space:nowrap">${currency} ${fmt(totals.overtime)}</td>
        <td style="text-align:right;padding:8px 8px;color:#ef4444;border:1px solid #e5e7eb;border-top:2px solid #111827;white-space:nowrap">${currency} ${fmt(totals.deductions)}</td>
        <td style="text-align:right;padding:8px 8px;color:#ef4444;border:1px solid #e5e7eb;border-top:2px solid #111827;white-space:nowrap">${currency} ${fmt(totals.contrib)}</td>
        <td style="text-align:right;padding:8px 8px;border:1px solid #e5e7eb;border-top:2px solid #111827;white-space:nowrap">${currency} ${fmt(totals.net)}</td>
        <td colspan="2" style="border:1px solid #e5e7eb;border-top:2px solid #111827"></td>
      </tr>
    </tbody>
  </table>

</div>`;
}

export async function generateEarningsPdf(
  items: IPayrollRunItem[],
  opts: {
    periodLabel: string;
    filename?: string;
    runName?: string;
  },
): Promise<void> {
  const now = new Date();
  const generatedAt = now.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const html = buildHtml(items, opts.periodLabel, generatedAt, opts.runName);

  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.top = "0";
  host.style.left = "-99999px";
  host.style.background = "#ffffff";
  host.innerHTML = html;
  document.body.appendChild(host);

  const inner = host.firstElementChild as HTMLElement;

  try {
    const width = Math.max(inner.scrollWidth, inner.offsetWidth, 1100);
    const height = Math.max(inner.scrollHeight, inner.offsetHeight);

    // Measure tbody row positions in DOM px before PNG render.
    // Used to snap page breaks to row boundaries so no row is cut mid-height.
    const innerTop = inner.getBoundingClientRect().top;
    const detailTable = inner.querySelector<HTMLElement>("#earnings-detail-table");
    const tbodyRows = detailTable
      ? Array.from(detailTable.querySelectorAll<HTMLElement>("tbody tr"))
      : [];
    const rowTopsDom = tbodyRows.map((tr) => tr.getBoundingClientRect().top - innerTop);

    const dataUrl = await toPng(inner, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
      cacheBust: true,
      width,
      height,
    });

    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error("Image load failed"));
    });

    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const maxW = pageW - margin * 2;
    const maxH = pageH - margin * 2;

    const ratio = Math.min(maxW / img.width, 1);
    const renderedW = img.width * ratio;
    const renderedH = img.height * ratio;
    const x = (pageW - renderedW) / 2;

    if (renderedH <= maxH) {
      pdf.addImage(dataUrl, "PNG", x, margin, renderedW, renderedH);
    } else {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;
      // image pixels per PDF point
      const pxPerPt = img.width / renderedW;
      // actual pixel ratio between DOM px and image px (toPng pixelRatio=2)
      const imgPixelRatio = img.width / width;
      // Convert DOM row tops → image pixel Y
      const rowTopsImg = rowTopsDom.map((y) => y * imgPixelRatio);
      // max page height in image pixels
      const maxHImg = maxH * pxPerPt;

      let pageStart = 0; // image px
      let pageIdx = 0;

      while (pageStart < img.height) {
        if (pageIdx > 0) pdf.addPage();

        const idealEnd = pageStart + maxHImg;
        let pageEnd: number;

        if (idealEnd >= img.height) {
          pageEnd = img.height;
        } else {
          // Find the start of the last row that begins before the ideal cut.
          // Break just before it so the row lands fully on the next page.
          let snapY = idealEnd;
          for (const rowTop of rowTopsImg) {
            if (rowTop > pageStart && rowTop <= idealEnd) {
              snapY = rowTop;
            }
          }
          // Avoid infinite loop when a single row is taller than a full page
          pageEnd = snapY > pageStart ? snapY : idealEnd;
        }

        const sliceH = Math.round(pageEnd - pageStart);
        canvas.width = img.width;
        canvas.height = sliceH;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, -Math.round(pageStart));

        const pageDataUrl = canvas.toDataURL("image/png");
        pdf.addImage(pageDataUrl, "PNG", x, margin, renderedW, sliceH / pxPerPt);

        pageStart = pageEnd;
        pageIdx += 1;
      }
    }

    const filename = opts.filename ?? `earnings-report-${opts.periodLabel.replace(/\s+/g, "-")}`;
    pdf.save(`${filename}.pdf`);
  } finally {
    document.body.removeChild(host);
  }
}
